import React, { useRef, useState, useCallback } from "react";
import { Move } from "lucide-react";

/**
 * Lightweight before/after image slider — pure CSS clip-path + a range input.
 * No external library. Mobile-friendly (touch + drag). Lazy-loaded images.
 */
function Slide({ before, after, beforeLabel, afterLabel, caption, location }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div className="bg-white border border-slate-200 overflow-hidden" data-testid="before-after-card">
      <div
        ref={ref}
        className="relative w-full select-none touch-none aspect-[16/10] cursor-ew-resize bg-slate-100"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-label={`Before and after comparison — ${caption}`}
      >
        {/* AFTER (full image underneath) */}
        <img
          src={after}
          alt={`${caption} — after`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        {/* BEFORE clipped overlay */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={before}
            alt={`${caption} — before`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 bg-slate-900/85 text-white text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 font-medium">
          {beforeLabel}
        </div>
        <div className="absolute top-4 right-4 bg-[#C5A059] text-white text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 font-medium">
          {afterLabel}
        </div>

        {/* Divider + handle */}
        <div
          className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
          style={{ left: `${pos}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center border border-slate-200">
            <Move size={18} className="text-slate-900 rotate-90" />
          </div>
        </div>
      </div>

      {/* Caption */}
      <div className="flex items-center justify-between gap-6 px-5 py-4 border-t border-slate-200">
        <div>
          <div className="font-display text-lg font-semibold text-slate-900">{caption}</div>
          <div className="overline mt-0.5 text-slate-500">{location}</div>
        </div>
        <div className="hidden sm:block text-[11px] tracking-[0.16em] uppercase text-slate-400">
          Drag to compare
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterSlider({ items }) {
  return (
    <section className="bg-slate-50 border-t border-slate-200" data-testid="before-after-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Before / After</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Real transformations.<br /> Drag to compare.
            </h2>
          </div>
          <p className="lg:col-span-5 text-slate-600 leading-relaxed">
            Every Prime Build project starts with a vision — and ends with a measurable transformation.
            These are real, in-progress and completed Prime Build sites across London.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {items.map((it, i) => (
            <Slide key={i} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}
